import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { decideDuplicate, errorMessage } from '../../api/catalogImportApi';
import { btnGhost, btnPrimary, card, overlay, pill } from './styles';

const inr = (v) => (v === null || v === undefined || v === '' ? '' : `₹${Number(v).toLocaleString('en-IN')}`);
const pct = (v) => (v === null || v === undefined ? '' : `${v}%`);
const dims = (l, w, h, u) => ([l, w, h].some((x) => x !== null && x !== undefined)
  ? `${l ?? '–'} × ${w ?? '–'} × ${h ?? '–'} ${u || ''}`.trim() : '');
const join = (...parts) => parts.filter(Boolean).join(' · ');

/** One row per updatable field: how to show the existing product's value and the catalog's. */
const FIELDS = [
  { key: 'name', label: 'Name', old: (p) => p.name, neu: (d) => d.name },
  { key: 'description', label: 'Description', old: (p) => p.description, neu: (d) => d.description, long: true },
  { key: 'mrp', label: 'MRP (incl. GST)', old: (p) => inr(p.mrp), neu: (d) => inr(d.mrp) },
  { key: 'marketOperatingPrice', label: 'MOP (excl. GST)', old: (p) => inr(p.marketOperatingPrice), neu: (d) => inr(d.marketOperatingPrice) },
  { key: 'eyantraPurchasePrice', label: 'EPP (excl. GST)', old: (p) => inr(p.eyantraPurchasePrice), neu: (d) => inr(d.eyantraPurchasePrice) },
  { key: 'gst', label: 'GST', old: (p) => pct(p.gst), neu: (d) => pct(d.gst) },
  { key: 'colors', label: 'Colours', old: (p) => p.colors, neu: (d) => d.colors },
  { key: 'sizes', label: 'Sizes', old: (p) => p.sizes, neu: (d) => d.sizes },
  { key: 'dimensions', label: 'Dimensions',
    old: (p) => dims(p.dimensionL, p.dimensionW, p.dimensionH, p.dimensionUnit),
    neu: (d) => dims(d.dimensionL, d.dimensionW, d.dimensionH, d.dimensionUnit) },
  { key: 'category', label: 'Category', old: (p) => join(p.category, p.subcategory), neu: (d) => join(d.categoryName, d.subcategoryName) },
  { key: 'seller', label: 'Seller / SPOC',
    old: (p) => join(p.sellerName, p.sellerContactInformation, p.spoc),
    neu: (d) => join(d.sellerName, d.sellerContactInformation, d.spoc) },
];

const IMAGE_MODES = [
  { key: 'KEEP', text: 'Keep current images' },
  { key: 'ADD', text: 'Add the catalog images' },
  { key: 'REPLACE', text: 'Replace with the catalog images' },
];

/**
 * "Keep existing, but update it from the catalog": old vs new, field by field. Nothing is ticked
 * by default — every overwrite is a deliberate choice, because the product is shared by the main
 * shop and every Brandstore.
 */
export default function UpdateExistingModal({ item, productId, campaign, onClose, onDone }) {
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [chosen, setChosen] = useState(() => new Set());
  const [imageMode, setImageMode] = useState('KEEP');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/products/${productId}`)
      .then((r) => setProduct(r.data))
      .catch((err) => setLoadError(errorMessage(err, 'Could not load the existing product.')));
  }, [productId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const rows = useMemo(() => (product ? FIELDS.map((f) => {
    const oldVal = f.old(product) ?? '';
    const newVal = f.neu(item) ?? '';
    return { ...f, oldVal, newVal, usable: newVal !== '' && String(newVal) !== String(oldVal) };
  }) : []), [product, item]);

  const toggle = (key) => setChosen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const nothingChosen = chosen.size === 0 && imageMode === 'KEEP';

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await decideDuplicate(item.id, { decision: 'UPDATE_EXISTING', productId, fields: [...chosen], imageMode });
      onDone(res.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not update the product.'));
      setSaving(false);
    }
  };

  const cell = { padding: '8px 10px', fontSize: 13, verticalAlign: 'top', borderTop: '1px solid #F3F4F6' };

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label={`Update product #${productId} from the catalog`}>
      <div style={{ ...card, width: 'min(900px, 100%)', maxHeight: '92vh', overflowY: 'auto', marginBottom: 0 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700 }}>Update existing product #{productId} from the catalog</h3>
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6B7280' }}>
          Tick what the catalog should overwrite. Everything else stays as it is.
          {campaign && <> The product is then added to <strong>{campaign.name}</strong>.</>}
        </p>

        <div role="note" style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#92400E', marginBottom: 12 }}>
          This changes the product everywhere it's sold — the main shop and every Brandstore, not just this campaign.
          {user?.role !== 'ADMIN' && <> <strong>As a curator, your update sends the product back for admin approval; it's hidden from the shop and Brandstores until approved.</strong></>}
        </div>

        {error && <div role="alert" style={{ color: '#B91C1C', fontSize: 13, marginBottom: 10 }}>{error}</div>}

        {loadError ? (
          <p role="alert" style={{ color: '#B91C1C' }}>{loadError}</p>
        ) : !product ? (
          <p style={{ color: '#9CA3AF' }}>Loading the existing product…</p>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                <thead>
                  <tr style={{ textAlign: 'left', fontSize: 11, color: '#9CA3AF' }}>
                    <th style={{ ...cell, borderTop: 'none', width: 32 }}><span className="sr-only">Update</span></th>
                    <th style={{ ...cell, borderTop: 'none', width: 130 }}>Field</th>
                    <th style={{ ...cell, borderTop: 'none' }}>Now</th>
                    <th style={{ ...cell, borderTop: 'none' }}>From catalog</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key} style={{ opacity: r.usable ? 1 : 0.55, background: chosen.has(r.key) ? '#FEF3E2' : undefined }}>
                      <td style={cell}>
                        <input type="checkbox" id={`upd-${r.key}`} disabled={!r.usable} checked={chosen.has(r.key)}
                               onChange={() => toggle(r.key)} style={{ width: 16, height: 16, accentColor: '#F29213' }} />
                      </td>
                      <td style={{ ...cell, fontWeight: 600 }}><label htmlFor={`upd-${r.key}`}>{r.label}</label></td>
                      <td style={{ ...cell, color: '#374151', whiteSpace: r.long ? 'pre-wrap' : undefined }}>
                        {r.long && r.oldVal.length > 240 ? `${r.oldVal.slice(0, 240)}…` : (r.oldVal || <em style={{ color: '#9CA3AF' }}>empty</em>)}
                      </td>
                      <td style={{ ...cell, color: '#111827', whiteSpace: r.long ? 'pre-wrap' : undefined }}>
                        {r.newVal === '' ? <em style={{ color: '#9CA3AF' }}>not in catalog</em>
                          : !r.usable ? <span style={pill('#F3F4F6', '#6B7280')}>same</span>
                          : r.long && r.newVal.length > 240 ? `${r.newVal.slice(0, 240)}…` : r.newVal}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <fieldset style={{ border: 'none', padding: 0, margin: '16px 0 0' }}>
              <legend style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Images</legend>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 4 }}>Now</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(product.imageUrls || []).slice(0, 5).map((u, i) => (
                      <img key={u} src={u} alt={`Current ${i + 1}`} style={{ width: 64, height: 64, objectFit: 'contain', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff' }} />
                    ))}
                    {!(product.imageUrls || []).length && <em style={{ fontSize: 12, color: '#9CA3AF' }}>none</em>}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 4 }}>From catalog</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(item.images || []).map((img, i) => (
                      <img key={img.id} src={img.url} alt={`Catalog crop ${i + 1}`} style={{ width: 64, height: 64, objectFit: 'contain', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff' }} />
                    ))}
                  </div>
                </div>
              </div>
              {IMAGE_MODES.map((m) => (
                <label key={m.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginRight: 18, cursor: 'pointer' }}>
                  <input type="radio" name={`img-mode-${item.id}`} value={m.key} checked={imageMode === m.key}
                         disabled={m.key !== 'KEEP' && !(item.images || []).length}
                         onChange={() => setImageMode(m.key)} style={{ accentColor: '#F29213' }} />
                  {m.text}
                </label>
              ))}
            </fieldset>
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button style={btnGhost} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...btnPrimary, opacity: saving || nothingChosen || !product ? 0.6 : 1 }}
                  disabled={saving || nothingChosen || !product} onClick={submit}
                  title={nothingChosen ? 'Tick a field or choose an image change' : undefined}>
            {saving ? 'Updating…' : `Update product${campaign ? ' & add to campaign' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
